import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Pagination,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import WorkIcon from '@mui/icons-material/Work';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { apiRequest } from '../../utils/api';

interface JobHistoryItem {
  id: number;
  job_id: number;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  user_id: number | null;
  description: string | null;
  created_at: string;
  user_name: string | null;
  user_email: string | null;
}

interface HistoryProps {
  jobId: number;
  asTab?: boolean;
  isActive?: boolean;
}

interface WorkerReportItem {
  id: number;
  job_id: number;
  task_id: number;
  user_id: number;
  report_type: string;
  description: string | null;
  created_at: string;
  user_name: string | null;
  user_email: string | null;
  files: Array<{
    id: number;
    filename: string;
    originalName: string;
    customName: string | null;
    fileUrl: string;
    mimeType: string;
  }>;
  images: Array<{
    id: number;
    imageUrl: string;
    imagePath: string;
  }>;
}

export default function History({ jobId, asTab = false, isActive = false }: HistoryProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [history, setHistory] = useState<JobHistoryItem[]>([]);
  const [workerReports, setWorkerReports] = useState<WorkerReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingWorkerReports, setLoadingWorkerReports] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [page, setPage] = useState(1);
  const [workerPage, setWorkerPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [workerTotalPages, setWorkerTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [workerTotal, setWorkerTotal] = useState(0);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const itemsPerPage = 50;

  useEffect(() => {
    if (isActive || !asTab) {
      setPage(1); // Reset to first page when jobId changes
    }
  }, [jobId, isActive, asTab]);

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/jobs/${jobId}/history?page=${page}&limit=${itemsPerPage}`, { method: 'GET' });
      
      // apiRequest returns axios response, so data is in response.data
      const responseData = response.data;
      
      // Check if response has pagination (new format)
      if (responseData && responseData.pagination) {
        const historyArray = Array.isArray(responseData.data) ? responseData.data : [];
        setHistory(historyArray);
        setTotalPages(responseData.pagination.totalPages || 1);
        setTotal(responseData.pagination.total || 0);
      } else {
        // Fallback for backward compatibility - responseData might be an array directly
        const historyArray = Array.isArray(responseData) ? responseData : [];
        setHistory(historyArray);
        setTotalPages(1);
        setTotal(historyArray.length);
      }
    } catch (error) {
      console.error('Error loading job history:', error);
      setHistory([]); // Ensure history is always an array
      setTotalPages(1);
      setTotal(0);
      setSnackbar({
        open: true,
        message: 'Failed to load job history',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [jobId, page, itemsPerPage]);

  const loadWorkerReports = useCallback(async () => {
    try {
      setLoadingWorkerReports(true);
      const response = await apiRequest(`/jobs/${jobId}/worker-reports?page=${workerPage}&limit=${itemsPerPage}`, { method: 'GET' });
      
      const responseData = response.data;
      
      if (responseData && responseData.pagination) {
        const reportsArray = Array.isArray(responseData.data) ? responseData.data : [];
        setWorkerReports(reportsArray);
        setWorkerTotalPages(responseData.pagination.totalPages || 1);
        setWorkerTotal(responseData.pagination.total || 0);
      } else {
        const reportsArray = Array.isArray(responseData) ? responseData : [];
        setWorkerReports(reportsArray);
        setWorkerTotalPages(1);
        setWorkerTotal(reportsArray.length);
      }
    } catch (error) {
      console.error('Error loading worker reports:', error);
      setWorkerReports([]);
      setWorkerTotalPages(1);
      setWorkerTotal(0);
    } finally {
      setLoadingWorkerReports(false);
    }
  }, [jobId, workerPage, itemsPerPage]);

  useEffect(() => {
    if (isActive || !asTab) {
      loadHistory();
      loadWorkerReports();
    }
  }, [isActive, asTab, loadHistory, loadWorkerReports]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const formatFieldName = (fieldName: string | null): string => {
    if (!fieldName) return 'General';
    // Convert snake_case to Title Case
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatValue = (value: string | null): string => {
    if (value === null || value === undefined) return '-';
    if (value === '') return '(empty)';
    return value;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleZoomIn = () => {
    setImageZoom((prev) => Math.min(prev + 0.25, 5)); // Max zoom 5x
  };

  const handleZoomOut = () => {
    setImageZoom((prev) => Math.max(prev - 0.25, 0.5)); // Min zoom 0.5x
  };

  const handleResetZoom = () => {
    setImageZoom(1);
    setImageRotation(0);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleRotateLeft = () => {
    setImageRotation((prev) => {
      const newRotation = prev - 90;
      return ((newRotation % 360) + 360) % 360; // Normalize to 0-360 range
    });
  };

  const handleRotateRight = () => {
    setImageRotation((prev) => (prev + 90) % 360);
  };

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate zoom delta - use a more responsive increment
    // Scroll down (deltaY > 0) = zoom out, scroll up (deltaY < 0) = zoom in
    const zoomSpeed = 0.15; // Increased for more responsive zooming
    const delta = e.deltaY > 0 ? -zoomSpeed : zoomSpeed;
    
    setImageZoom((prev) => {
      const newZoom = Math.max(0.5, Math.min(5, prev + delta));
      return newZoom;
    });
  }, []);

  // Attach wheel event listener with passive: false to allow preventDefault
  useEffect(() => {
    if (!selectedImageUrl) return;

    // Attach to document for better reliability when modal is open
    // This ensures the event listener works even if Material-UI components don't forward refs properly
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, [selectedImageUrl, handleWheel]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (imageZoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && imageZoom > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleImageClose = () => {
    setSelectedImageUrl(null);
    setImageZoom(1);
    setImageRotation(0);
    setImagePosition({ x: 0, y: 0 });
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange} variant={isMobile ? 'fullWidth' : 'standard'}>
              <Tab icon={<HistoryIcon />} iconPosition="start" label={isMobile ? 'Job History' : 'Job Changing History'} />
              <Tab icon={<WorkIcon />} iconPosition="start" label="Worker History" />
            </Tabs>
          </Box>

          {activeTab === 0 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Job Changing History</Typography>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : history.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography color="text.secondary">No history records found</Typography>
                </Box>
              ) : (
                <>
                  {isMobile ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {history.map((item) => (
                        <Paper key={item.id} variant="outlined" sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Chip
                              label={formatFieldName(item.field_name)}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(item.created_at)}
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">Old Value</Typography>
                              <Typography variant="body2" sx={{ fontStyle: item.old_value ? 'normal' : 'italic', color: 'text.secondary' }}>
                                {formatValue(item.old_value)}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">New Value</Typography>
                              <Typography variant="body2" sx={{ fontStyle: item.new_value ? 'normal' : 'italic', fontWeight: 'medium' }}>
                                {formatValue(item.new_value)}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              By: {item.user_name || item.user_email || `User ${item.user_id || 'Unknown'}`}
                            </Typography>
                            {item.description && (
                              <Typography variant="caption" color="text.secondary">
                                {item.description}
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date & Time</TableCell>
                          <TableCell>Field</TableCell>
                          <TableCell>Old Value</TableCell>
                          <TableCell>New Value</TableCell>
                          <TableCell>Changed By</TableCell>
                          <TableCell>Description</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.map((item) => (
                          <TableRow key={item.id} hover>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(item.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={formatFieldName(item.field_name)}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.secondary',
                                  fontStyle: item.old_value ? 'normal' : 'italic',
                                }}
                              >
                                {formatValue(item.old_value)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.primary',
                                  fontWeight: 'medium',
                                  fontStyle: item.new_value ? 'normal' : 'italic',
                                }}
                              >
                                {formatValue(item.new_value)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {item.user_name || item.user_email || `User ${item.user_id || 'Unknown'}`}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {item.description || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  )}
                  {history.length > 0 && (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mt: 3,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        gap: 2 
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Showing {((page - 1) * itemsPerPage) + 1} to {Math.min(page * itemsPerPage, total)} of {total} records
                      </Typography>
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                        size="medium"
                      />
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === 1 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Worker History</Typography>
              </Box>

              {loadingWorkerReports ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : workerReports.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography color="text.secondary">No worker reports found</Typography>
                </Box>
              ) : (
                <>
                  {(() => {
                    const getImageUrl = (image: { imageUrl: string }) =>
                      image.imageUrl.startsWith('/api/')
                        ? image.imageUrl
                        : `/api${image.imageUrl}`;

                    return isMobile ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {workerReports.map((report) => (
                          <Paper key={report.id} variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Chip
                                label={report.report_type === 'file' ? 'Files' : 'Images'}
                                size="small"
                                color={report.report_type === 'file' ? 'primary' : 'secondary'}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(report.created_at)}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {report.user_name || report.user_email || `User ${report.user_id}`}
                            </Typography>
                            {report.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {report.description}
                              </Typography>
                            )}
                            {report.files && report.files.length > 0 && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Files</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {report.files.map((file) => (
                                    <Chip
                                      key={file.id}
                                      label={file.customName || file.originalName || file.filename}
                                      component="a"
                                      href={file.fileUrl}
                                      target="_blank"
                                      clickable
                                      size="small"
                                    />
                                  ))}
                                </Box>
                              </>
                            )}
                            {report.images && report.images.length > 0 && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Images</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {report.images.map((image) => {
                                    const imageUrl = getImageUrl(image);
                                    return (
                                      <Box
                                        key={image.id}
                                        component="img"
                                        src={imageUrl}
                                        alt={`Report image ${image.id}`}
                                        loading="lazy"
                                        sx={{
                                          width: 72,
                                          height: 72,
                                          objectFit: 'cover',
                                          cursor: 'pointer',
                                          borderRadius: 1,
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          '&:hover': { opacity: 0.8 },
                                        }}
                                        onClick={() => setSelectedImageUrl(imageUrl)}
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                      />
                                    );
                                  })}
                                </Box>
                              </>
                            )}
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date & Time</TableCell>
                            <TableCell>Reporter</TableCell>
                            <TableCell>Report Type</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Files</TableCell>
                            <TableCell>Images</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {workerReports.map((report) => (
                            <TableRow key={report.id} hover>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatDate(report.created_at)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {report.user_name || report.user_email || `User ${report.user_id}`}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={report.report_type === 'file' ? 'Files' : 'Images'}
                                  size="small"
                                  color={report.report_type === 'file' ? 'primary' : 'secondary'}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {report.description || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {report.files && report.files.length > 0 ? (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {report.files.map((file) => (
                                      <Chip
                                        key={file.id}
                                        label={file.customName || file.originalName || file.filename}
                                        component="a"
                                        href={file.fileUrl}
                                        target="_blank"
                                        clickable
                                        size="small"
                                      />
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {report.images && report.images.length > 0 ? (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {report.images.map((image) => {
                                      const imageUrl = getImageUrl(image);
                                      return (
                                        <Box
                                          key={image.id}
                                          component="img"
                                          src={imageUrl}
                                          alt={`Report image ${image.id}`}
                                          loading="lazy"
                                          sx={{
                                            width: 60,
                                            height: 60,
                                            objectFit: 'cover',
                                            cursor: 'pointer',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            '&:hover': { opacity: 0.8 },
                                          }}
                                          onClick={() => setSelectedImageUrl(imageUrl)}
                                          onError={(e) => {
                                            console.error('Failed to load image:', imageUrl);
                                            (e.target as HTMLImageElement).style.display = 'none';
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    );
                  })()}
                  {workerReports.length > 0 && (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mt: 3,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        gap: 2 
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Showing {((workerPage - 1) * itemsPerPage) + 1} to {Math.min(workerPage * itemsPerPage, workerTotal)} of {workerTotal} records
                      </Typography>
                      <Pagination
                        count={workerTotalPages}
                        page={workerPage}
                        onChange={(_e, value) => setWorkerPage(value)}
                        color="primary"
                        showFirstButton
                        showLastButton
                        size="medium"
                      />
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Image Modal */}
      <Dialog
        open={!!selectedImageUrl}
        onClose={handleImageClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
            maxWidth: '100vw',
            maxHeight: '100vh',
            margin: 0,
            width: '100vw',
            height: '100vh',
          },
        }}
      >
        <DialogContent 
          ref={dialogContentRef}
          sx={{ 
            p: 0, 
            position: 'relative', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '100vh',
            overflow: 'hidden',
            cursor: imageZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleImageClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
              zIndex: 2,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Zoom Controls */}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              zIndex: 2,
            }}
          >
            <IconButton
              onClick={handleZoomIn}
              disabled={imageZoom >= 5}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
                '&:disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
              title="Zoom In"
            >
              <ZoomInIcon />
            </IconButton>
            <IconButton
              onClick={handleZoomOut}
              disabled={imageZoom <= 0.5}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
                '&:disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
              title="Zoom Out"
            >
              <ZoomOutIcon />
            </IconButton>
            <IconButton
              onClick={handleResetZoom}
              disabled={imageZoom === 1 && imagePosition.x === 0 && imagePosition.y === 0 && imageRotation === 0}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
                '&:disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
              title="Reset Zoom"
            >
              <RestartAltIcon />
            </IconButton>
            <IconButton
              onClick={handleRotateLeft}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
              title="Rotate Left (90°)"
            >
              <RotateLeftIcon />
            </IconButton>
            <IconButton
              onClick={handleRotateRight}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
              title="Rotate Right (90°)"
            >
              <RotateRightIcon />
            </IconButton>
          </Box>

          {/* Zoom Level Indicator */}
          {imageZoom !== 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                padding: '4px 12px',
                borderRadius: 1,
                zIndex: 2,
              }}
            >
              <Typography variant="body2">
                {Math.round(imageZoom * 100)}%
              </Typography>
            </Box>
          )}

          {/* Image */}
          {selectedImageUrl && (
            <Box
              ref={imageRef}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <Box
                component="img"
                src={selectedImageUrl}
                alt="Full size image"
                onMouseDown={handleMouseDown}
                sx={{
                  maxWidth: imageZoom > 1 ? 'none' : '100%',
                  maxHeight: imageZoom > 1 ? 'none' : '90vh',
                  width: imageZoom > 1 ? `${imageZoom * 100}%` : 'auto',
                  height: imageZoom > 1 ? `${imageZoom * 100}%` : 'auto',
                  objectFit: 'contain',
                  transform: `translate(${imagePosition.x}px, ${imagePosition.y}px) rotate(${imageRotation}deg)`,
                  transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                  userSelect: 'none',
                  transformOrigin: 'center center',
                }}
                onError={(e) => {
                  console.error('Failed to load image:', selectedImageUrl);
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
